import { useState } from 'react';
import { Modal, ModalContent, ModalFooter } from '@finn-no/troika-react-modal';
import '@finn-no/troika-react-modal/style.css';

import displayValues from '@/assets/displayValues.json';
import { thousandSeparated } from 'shared/util/formatting';

import type { HotelReviews, Rating } from '@/interfaces';
import { GuestRatingCircle } from '../guestRatingCircle/GuestRatingCircle';
import { GuestRatingBar } from './components/GuestRatingBar/GuestRatingBar';

export type GuestRatingModalProps = Pick<HotelReviews, 'numberOfReviews'> & { guestRatings: Rating[] };

export const GuestRatingModal = ({ guestRatings, numberOfReviews = 0 }: GuestRatingModalProps) => {
    const [open, setOpen] = useState(false);

    const overallGuestRating = guestRatings.find((rating) => rating.key.toLowerCase() === 'overall')?.value;
    const otherGuestRatings = guestRatings.filter((rating) => rating.key.toLowerCase() !== 'overall');

    return (
        <>
            <button
                type="button"
                data-testid="guest-rating-more-button"
                className="button button--small button--link u-pa0 button--link--strong modal-button"
                onClick={() => setOpen(true)}>
                Se detaljer
            </button>
            <Modal isOpen={open} className="guest-rating-modal" aria-labelledby="guest-rating-modal-header">
                <ModalContent>
                    <h2 className="u-t3 u-mv32" id="guest-rating-modal-header">
                        Gjestevurderinger
                    </h2>
                    <div className="flex flex-align-center flex-justify-start u-size1of1 u-mv32">
                        <GuestRatingCircle guestRating={overallGuestRating} large />
                        <div className="u-ml16">
                            <div data-testid="guestSentiment" className="u-t4 u-mb4--gt768">
                                {displayValues.guestRatingSentiments[Math.floor(overallGuestRating)]}
                            </div>
                            <div data-testid="numberOfReviews" className="u-d1">
                                ({thousandSeparated(numberOfReviews)} vurderinger)
                            </div>
                        </div>
                    </div>
                    {otherGuestRatings.map((rating) => (
                        <GuestRatingBar
                            data-testid={`guest-rating-${rating.key}`}
                            key={`guest-rating-${rating.key}`}
                            ratingKey={rating.key}
                            ratingValue={rating.value}
                        />
                    ))}
                </ModalContent>
                <ModalFooter className="flex-align-end">
                    <div className="u-d1 u-mr16">
                        Gjestevurderingene er basert på anmeldelser fra flere hotellsider, blant annet Hotels.com og Booking.com
                    </div>
                    <button
                        type="button"
                        data-testid="guest-rating-close-button"
                        onClick={() => setOpen(false)}
                        className="button flex-no-shrink">
                        Lukk
                    </button>
                </ModalFooter>
            </Modal>
        </>
    );
};
